import { useState } from "react";
import { apiFetch } from "@/lib/api-client";

const TONE_CARDS = [
  {
    id: "professional",
    name: "Professional",
    icon: "💼",
    description: "Polished and business-appropriate",
    example: "We would be delighted to assist you with your upcoming appointment.",
  },
  {
    id: "casual",
    name: "Casual",
    icon: "☕",
    description: "Relaxed and approachable",
    example: "Hey! Just a quick heads-up about your appointment tomorrow.",
  },
  {
    id: "friendly",
    name: "Friendly",
    icon: "😊",
    description: "Warm and personable",
    example: "Hi there! We are so excited to see you soon for your visit.",
  },
  {
    id: "authoritative",
    name: "Authoritative",
    icon: "🎯",
    description: "Confident and expert-driven",
    example: "Based on our expertise, here is what we recommend for your next session.",
  },
  {
    id: "playful",
    name: "Playful",
    icon: "🎉",
    description: "Fun and energetic",
    example: "Guess what? Your appointment is coming up and we cannot wait to see you!",
  },
  {
    id: "technical",
    name: "Technical",
    icon: "🔬",
    description: "Precise and detail-oriented",
    example: "Your scheduled procedure is confirmed for 10:00 AM. Please review the attached preparation guide.",
  },
] as const;

type ToneId = (typeof TONE_CARDS)[number]["id"] | "custom";

interface SampleResponse {
  sample: string;
}

export default function BrandVoicePage() {
  const [selectedTone, setSelectedTone] = useState<ToneId | null>(null);
  const [customDescription, setCustomDescription] = useState("");
  const [sampleEmail, setSampleEmail] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSelectTone = async (tone: ToneId) => {
    setSelectedTone(tone);
    if (tone === "custom") {
      setSampleEmail(null);
      return;
    }
    await generateSample(tone);
  };

  const generateSample = async (tone: string, description?: string) => {
    setIsGenerating(true);
    setSampleEmail(null);
    try {
      const result = await apiFetch<SampleResponse>("/api/onboarding/brand-voice/sample", {
        method: "POST",
        body: JSON.stringify({
          tone,
          businessName: "",
          industry: "",
          ...(description ? { customDescription: description } : {}),
        }),
      });
      setSampleEmail(result.sample);
    } catch {
      setSampleEmail("Unable to generate sample. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTryAnother = () => {
    if (selectedTone && selectedTone !== "custom") {
      generateSample(selectedTone);
    } else if (selectedTone === "custom" && customDescription.trim()) {
      generateSample("custom", customDescription);
    }
  };

  const handleSave = async () => {
    if (!selectedTone) return;
    setIsSaving(true);
    try {
      await apiFetch("/api/onboarding/brand-voice/save", {
        method: "POST",
        body: JSON.stringify({
          tone: selectedTone,
          customDescription: selectedTone === "custom" ? customDescription : undefined,
        }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="mx-auto w-full max-w-2xl px-6 py-8">
        <h1 className="text-2xl font-bold text-foreground">Choose your brand voice</h1>
        <p className="mt-1 text-muted-foreground">
          This sets the tone for all AI-generated emails, messages, and content.
        </p>

        {/* Tone selection group */}
        <div role="radiogroup" aria-label="Brand voice tone selection">
          {/* Tone grid */}
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {TONE_CARDS.map((card) => (
              <button
                key={card.id}
                role="radio"
                aria-checked={selectedTone === card.id}
                onClick={() => handleSelectTone(card.id)}
                className={`flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors ${
                  selectedTone === card.id
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <span className="text-2xl">{card.icon}</span>
                <span className="text-sm font-semibold text-foreground">{card.name}</span>
                <span className="text-xs text-muted-foreground">{card.description}</span>
                <span className="mt-1 text-xs italic text-muted-foreground/70">"{card.example}"</span>
              </button>
            ))}
          </div>

          {/* Custom option */}
          <div className="mt-4">
            <button
              role="radio"
              aria-checked={selectedTone === "custom"}
              onClick={() => handleSelectTone("custom")}
              className={`w-full rounded-lg border p-4 text-left transition-colors ${
                selectedTone === "custom"
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <span className="text-sm font-semibold text-foreground">Custom</span>
              <span className="ml-2 text-xs text-muted-foreground">Describe your brand voice in your own words</span>
            </button>
            {selectedTone === "custom" && (
              <div className="mt-3 space-y-2">
                <label htmlFor="custom-brand-voice-description" className="block text-sm font-medium text-foreground">
                  Describe your brand voice
                </label>
                <textarea
                  id="custom-brand-voice-description"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="Describe how you want your brand to sound..."
                  rows={3}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
                />
                <button
                  onClick={() => customDescription.trim() && generateSample("custom", customDescription)}
                  disabled={!customDescription.trim() || isGenerating}
                  className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50"
                >
                  Generate Sample
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sample preview */}
        {(isGenerating || sampleEmail) && (
          <div
            className="mt-6 rounded-lg border border-border bg-muted/30 p-4"
            aria-live="polite"
            aria-busy={isGenerating}
          >
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              AI-Generated Sample Email
            </h3>
            {isGenerating ? (
              <div className="flex items-center gap-2 py-4" aria-label="Generating sample email">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">Generating...</span>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-sm text-foreground">{sampleEmail}</p>
            )}
          </div>
        )}

        {/* Actions */}
        {sampleEmail && !isGenerating && (
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save to Brand Kit"}
            </button>
            <button
              onClick={handleTryAnother}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
            >
              Try Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
